// frontend/src/utils/estimativaPontosCartao.js

/**
 * Calibração interna: bancos costumam usar dólar acima da cotação de fechamento.
 * Não expor este fator na UI.
 */
const SPREAD_DOLAR_ESTIMATIVA_PONTOS = 0.11;

/** Dias após o vencimento em que os pontos da fatura costumam ser creditados. */
export const DIAS_CREDITO_PONTOS_APOS_VENCIMENTO = 5;

/**
 * Estima pontos de fatura a partir da regra do cartão.
 * @returns {{ pontos: number|null, detalhe: string }}
 */
export function estimarPontosFatura({
  valorFatura,
  regraPontos,
  pontosPorUnidade,
  cotacaoBid,
}) {
  const valor = Number(valorFatura);
  const pts = Number(pontosPorUnidade);
  if (!Number.isFinite(valor) || valor <= 0 || !Number.isFinite(pts) || pts <= 0) {
    return { pontos: null, detalhe: '' };
  }

  if (regraPontos === 'POR_REAL') {
    const pontos = Math.floor(valor * pts);
    return {
      pontos,
      detalhe: `R$ ${valor.toFixed(2)} × ${pts} pts/R$ ≈ ${pontos.toLocaleString('pt-BR')} pontos`,
    };
  }

  if (regraPontos === 'POR_DOLAR') {
    const bid = Number(cotacaoBid);
    if (!Number.isFinite(bid) || bid <= 0) {
      return { pontos: null, detalhe: 'Cotação do dólar indisponível — informe a quantidade manualmente.' };
    }
    const cotacaoEfetiva = bid * (1 + SPREAD_DOLAR_ESTIMATIVA_PONTOS);
    const usd = valor / cotacaoEfetiva;
    const pontos = Math.floor(usd * pts);
    return {
      pontos,
      detalhe: `Estimativa aproximada ≈ ${pontos.toLocaleString('pt-BR')} pontos`,
    };
  }

  return { pontos: null, detalhe: '' };
}

const CATEGORIA_PAGAMENTO_PARCIAL = 'Pagamento Avulso de Fatura';

/** Soma valores de pagamentos parciais da fatura (receitas de pagamento avulso). */
export function somarPagamentosParciaisFatura(transacoes = []) {
  if (!Array.isArray(transacoes)) return 0;
  return transacoes.reduce((acc, t) => {
    if (t?.categoria_nome !== CATEGORIA_PAGAMENTO_PARCIAL) return acc;
    const v = Number(t.valor);
    return acc + (Number.isFinite(v) ? Math.abs(v) : 0);
  }, 0);
}

/** Base para estimar pontos: saldo restante + pagamentos parciais já feitos. */
export function valorBasePontosFatura(valorTotal, totalParciais = 0) {
  const restante = Number(valorTotal) || 0;
  const parciais = Number(totalParciais) || 0;
  return Math.max(0, restante + parciais);
}

/** Marcador estável na observação para vincular pontos pendentes à fatura (interno). */
const FATURA_ID_MARKER_RE = /\[FATURA_ID:\d+\]/g;

export function marcadorFaturaId(faturaId) {
  if (faturaId == null || faturaId === '') return '';
  return `[FATURA_ID:${faturaId}]`;
}

/** Remove marcadores internos para exibição ao usuário. */
export function stripMarcadorFaturaId(observacao) {
  return String(observacao || '')
    .replace(FATURA_ID_MARKER_RE, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export function extractFaturaIdFromObservacao(observacao) {
  const match = String(observacao || '').match(/\[FATURA_ID:(\d+)\]/);
  return match ? match[1] : null;
}

/** Texto visível ao usuário (sem marcador). */
export function observacaoPontosFatura({ nomeCartao, mes, ano }) {
  const mesPad = String(mes ?? '').padStart(2, '0');
  return `Pontos estimados — fatura ${nomeCartao || 'cartão'} ${mesPad}/${ano}`;
}

/** Garante marcador interno na observação enviada à API (não deve ser mostrado na UI). */
export function ensureObservacaoComFaturaId(observacao, faturaId) {
  const marker = marcadorFaturaId(faturaId);
  const obs = stripMarcadorFaturaId(observacao);
  if (!marker) return obs;
  return obs ? `${obs} ${marker}` : marker;
}

/** Ao editar, mantém o marcador original se o usuário não o vê no formulário. */
export function preserveMarcadorFaturaId(observacaoEditada, observacaoOriginal) {
  const faturaId = extractFaturaIdFromObservacao(observacaoOriginal);
  if (!faturaId) return stripMarcadorFaturaId(observacaoEditada);
  return ensureObservacaoComFaturaId(observacaoEditada, faturaId);
}

export function prefixoObservacaoPontosFatura({ nomeCartao, mes, ano }) {
  const mesPad = String(mes ?? '').padStart(2, '0');
  return `Pontos estimados — fatura ${nomeCartao || 'cartão'} ${mesPad}/${ano}`;
}
